"use client";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { type ReactNode, createContext, useContext, useMemo } from "react";

export type PointerInteractionMedia = {
  canHover: boolean;
  hasPointerFine: boolean;
  prefersReducedMotion: boolean;
};

const PointerInteractionMediaContext = createContext<PointerInteractionMedia | null>(null);

export function PointerInteractionMediaProvider({ children }: { children: ReactNode }) {
  const canHover = useMediaQuery("(hover: hover)");
  const hasPointerFine = useMediaQuery("(pointer: fine)");
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const value = useMemo(
    () => ({ canHover, hasPointerFine, prefersReducedMotion }),
    [canHover, hasPointerFine, prefersReducedMotion]
  );

  return (
    <PointerInteractionMediaContext.Provider value={value}>
      {children}
    </PointerInteractionMediaContext.Provider>
  );
}

export function usePointerInteractionMediaOptional(): PointerInteractionMedia | null {
  return useContext(PointerInteractionMediaContext);
}

export function usePointerInteractionMedia(): PointerInteractionMedia {
  const value = usePointerInteractionMediaOptional();
  if (!value) {
    throw new Error(
      "usePointerInteractionMedia must be used within PointerInteractionMediaProvider or PointerInteractionAutoProvider"
    );
  }
  return value;
}
